/* eslint-disable no-empty */
// ============================================================
// ADMIN — Images Manager
// Paste a code [GATE33-DXX-MYY-LABEL-IMG-NN], upload a file, save.
// View / replace / delete all coded images.
// ============================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Loader2, Trash2, RefreshCw, ImageIcon, ArrowLeft, CheckCircle2, AlertCircle, Download, Archive, FileText } from 'lucide-react';
import { useProgram } from './ProgramContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'g33_program_token';
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } };

const CODE_RE = /^\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\]$/;

const KNOWN_CODES = [
  // Aircraft fleet (Module 11) — populated automatically when listing
  '[GATE33-D03-M11-AIRCRAFT-CHALLENGER350-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-CHALLENGER650-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-GLOBAL6000-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-GLOBAL7500-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-G550-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-G650-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-G700-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-FALCON2000-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-FALCON7X-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-FALCON8X-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-FALCON10X-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-BBJ-IMG-01]',
  '[GATE33-D03-M11-AIRCRAFT-ACJ-IMG-01]',
  // Cabin layout slots (Module 11) — uploaded illustrations of cabin floor plans
  '[GATE33-D03-M11-CABIN-CHALLENGER350-IMG-01]',
  '[GATE33-D03-M11-CABIN-CHALLENGER650-IMG-01]',
  '[GATE33-D03-M11-CABIN-GLOBAL6000-IMG-01]',
  '[GATE33-D03-M11-CABIN-GLOBAL7500-IMG-01]',
  '[GATE33-D03-M11-CABIN-G550-IMG-01]',
  '[GATE33-D03-M11-CABIN-G650-IMG-01]',
  '[GATE33-D03-M11-CABIN-G700-IMG-01]',
  '[GATE33-D03-M11-CABIN-FALCON2000-IMG-01]',
  '[GATE33-D03-M11-CABIN-FALCON7X-IMG-01]',
  '[GATE33-D03-M11-CABIN-FALCON8X-IMG-01]',
  '[GATE33-D03-M11-CABIN-FALCON10X-IMG-01]',
  '[GATE33-D03-M11-CABIN-BBJ-IMG-01]',
  '[GATE33-D03-M11-CABIN-ACJ-IMG-01]',
];

const AdminImages = () => {
  const { images, refreshImages, modules } = useProgram();
  const [code, setCode] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const fileRef = useRef(null);
  const [filter, setFilter] = useState('');

  useEffect(() => { refreshImages(); }, [refreshImages]);

  // Scan all module contents for codes appearing inline + add KNOWN_CODES + module hero codes
  const referenceCodes = useMemo(() => {
    const set = new Set(KNOWN_CODES);
    // Hero code for each module
    for (const m of (modules || [])) {
      const day = String(m.day || 1).padStart(2, '0');
      const mod = String(m.module_number || 1).padStart(2, '0');
      set.add(`[GATE33-D${day}-M${mod}-HERO-IMG-01]`);
    }
    const fields = ['hook', 'objective', 'trainer_narrative', 'operational_insight', 'participant_interaction', 'pull_quote'];
    const re = /\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\]/g;
    const scan = (v) => {
      if (typeof v === 'string') {
        const matches = v.match(re) || [];
        for (const c of matches) set.add(c);
      } else if (Array.isArray(v)) {
        for (const x of v) scan(x);
      } else if (v && typeof v === 'object') {
        for (const x of Object.values(v)) scan(x);
      }
    };
    for (const m of (modules || [])) {
      for (const f of fields) scan(m[f]);
      // Demonstration block
      scan(m.demonstration);
      // Narrative end note (e.g. Module 2 cabin-crew vs cabin-attendant block)
      if (m.narrative_end_note) scan(m.narrative_end_note);
      // Objective note
      if (m.objective_note) scan(m.objective_note);
    }
    // Include any already-uploaded codes
    Object.keys(images || {}).forEach(c => set.add(c));
    return Array.from(set).sort();
  }, [modules, images]);

  const filteredCodes = useMemo(() => {
    const q = filter.trim().toUpperCase();
    if (!q) return referenceCodes;
    return referenceCodes.filter(c => c.toUpperCase().includes(q));
  }, [referenceCodes, filter]);

  const filledCount = referenceCodes.filter(c => images?.[c]).length;
  const emptyCount = referenceCodes.length - filledCount;

  const onPick = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setMsg({ type: 'error', text: 'Please select an image.' }); return; }
    if (f.size > 8 * 1024 * 1024) { setMsg({ type: 'error', text: 'Image too large (max 8MB).' }); return; }
    setFile(f);
    setMsg(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setMsg(null);
    const c = code.trim();
    if (!CODE_RE.test(c)) {
      setMsg({ type: 'error', text: 'Invalid code format. Must look like [GATE33-D01-M01-LABEL-IMG-01].' });
      return;
    }
    if (!file) {
      setMsg({ type: 'error', text: 'Pick an image file first.' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('code', c);
      fd.append('caption', caption);
      fd.append('file', file);
      const r = await fetch(`${API}/api/gate33-program/images`, {
        method: 'POST',
        body: fd,
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `Upload failed (${r.status})`);
      }
      setMsg({ type: 'success', text: `Uploaded: ${c}` });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await refreshImages();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Remove uploaded image for ${c}?`)) return;
    try {
      const r = await fetch(`${API}/api/gate33-program/images/${encodeURIComponent(c)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      await refreshImages();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Delete failed' });
    }
  };

  const selectCode = (c) => {
    setCode(c);
    setCaption(images?.[c]?.caption || '');
    setMsg(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============================================================
  // BACKUP / RESTORE — export & import the full image library
  // ============================================================
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);

  const doExport = async (day = null) => {
    setExporting(true);
    setMsg(null);
    try {
      const qs = day ? `?day=${day}` : '';
      const r = await fetch(`${API}/api/gate33-program/admin/images-export${qs}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `Export failed (${r.status})`);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `gate33-program-images-${Date.now()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg({ type: 'success', text: `Exported ${fname} — keep this file safe.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Export failed' });
    } finally {
      setExporting(false);
    }
  };

  const onImportPick = () => importFileRef.current?.click();

  // ============================================================
  // EXPORT TRAINING CONTENT — PDF + DOCX + Markdown (no images)
  // ============================================================
  const [trainingExporting, setTrainingExporting] = useState(false);
  const doExportTraining = async () => {
    setTrainingExporting(true);
    setMsg(null);
    try {
      const r = await fetch(`${API}/api/gate33-program/admin/training-export`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `Export failed (${r.status})`);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `gate33-academy-3day-export-${Date.now()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg({ type: 'success', text: `Training content exported as ${fname}.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Training export failed' });
    } finally {
      setTrainingExporting(false);
    }
  };

  // PDF per day with embedded illustrations (compressed)
  const [pdfExportingDay, setPdfExportingDay] = useState(null);
  const doExportPdfDay = async (day) => {
    setPdfExportingDay(day);
    setMsg(null);
    try {
      const r = await fetch(`${API}/api/gate33-program/admin/training-export-pdf?day=${day}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `PDF export failed (${r.status})`);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `gate33-academy-day${day}-with-images.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg({ type: 'success', text: `Day ${day} PDF (with images) exported as ${fname}.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'PDF export failed' });
    } finally {
      setPdfExportingDay(null);
    }
  };
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setMsg({ type: 'error', text: 'Please pick the .zip file produced by Export.' });
      return;
    }
    if (!window.confirm(
      `Restore ${f.name}?\n\nThis will overwrite any existing image record with the same code. Existing images not in this zip will NOT be deleted.`
    )) {
      e.target.value = '';
      return;
    }
    setImporting(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/api/gate33-program/admin/images-import`, {
        method: 'POST',
        body: fd,
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Import failed (${r.status})`);
      setMsg({
        type: 'success',
        text: `Restored ${data.records_written}/${data.total_in_zip} images (${data.files_written} files written).`,
      });
      await refreshImages();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Import failed' });
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  return (
    <section style={{ padding: '120px 32px 80px', maxWidth: 1280, margin: '0 auto' }} data-testid="admin-images-page">
      <Link to="/gate33-program" className="g33-btn-ghost" style={{ marginBottom: 24 }}>
        <ArrowLeft size={14} /> Back to program
      </Link>
      <div className="g33-eyebrow" style={{ marginBottom: 12 }}>Admin · Images</div>
      <h1 className="g33-display" style={{ fontSize: 'clamp(32px, 5vw, 56px)', margin: '0 0 12px', lineHeight: 1.1 }}>
        Image manager
      </h1>
      <p className="g33-body" style={{ color: 'var(--g33-charcoal)', maxWidth: 720, marginBottom: 40 }}>
        Each visual slot in the program is identified by a code like{' '}
        <code style={{ fontFamily: 'ui-monospace, "SF Mono", Consolas, monospace', background: 'rgba(201,169,97,0.12)', padding: '2px 6px' }}>
          [GATE33-D01-M01-OPENING-IMG-01]
        </code>
        . Paste the code, pick an image file, and the placeholder in the corresponding module will be replaced.
      </p>

      {/* BACKUP / RESTORE — migrate image library between environments */}
      <div
        className="g33-trainer-block"
        style={{
          padding: 28,
          marginBottom: 40,
          borderColor: 'var(--g33-or-deep)',
          background: 'linear-gradient(180deg, rgba(201,169,97,0.05), transparent)',
        }}
        data-testid="admin-backup-block"
      >
        <div className="g33-eyebrow" style={{ marginBottom: 10, color: 'var(--g33-or-deep)' }}>
          Backup & Restore
        </div>
        <p className="g33-body" style={{ margin: '0 0 18px', color: 'var(--g33-charcoal)', fontSize: 14, lineHeight: 1.6 }}>
          Export the image library as 3 separate ZIPs — one per training day (kept under 100 MB to clear Cloudflare's upload limit). Download each one, then import them one by one on the target environment.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          {[1, 2, 3].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => doExport(d)}
              disabled={exporting}
              className="g33-btn-primary"
              data-testid={`admin-export-day${d}-btn`}
              style={{ padding: '11px 20px', fontSize: 11 }}
            >
              {exporting
                ? <><Loader2 size={13} className="g33-spin" /> Preparing…</>
                : <><Download size={13} /> Export Day {d}</>}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--g33-grey-warm)', marginLeft: 4 }}>
            {filledCount} images stored
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', paddingTop: 14, borderTop: '1px solid rgba(201,169,97,0.25)' }}>
          <span style={{ fontSize: 11, color: 'var(--g33-grey-warm)', textTransform: 'uppercase', letterSpacing: '0.16em' }}>
            Restore on this server:
          </span>
          <button
            type="button"
            onClick={onImportPick}
            disabled={importing}
            className="g33-btn-ghost"
            data-testid="admin-import-btn"
            style={{ padding: '11px 20px', fontSize: 11 }}
          >
            {importing
              ? <><Loader2 size={13} className="g33-spin" /> Restoring…</>
              : <><Archive size={13} /> Import a ZIP</>}
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept=".zip,application/zip"
            onChange={onImportFile}
            style={{ display: 'none' }}
            data-testid="admin-import-file-input"
          />
        </div>
      </div>

      {/* TRAINING CONTENT EXPORT — PDF + DOCX + Markdown */}
      <div
        className="g33-trainer-block"
        style={{
          padding: 28,
          marginBottom: 40,
          borderColor: 'var(--g33-or-deep)',
          background: 'linear-gradient(180deg, rgba(15,15,15,0.04), transparent)',
        }}
        data-testid="admin-training-export-block"
      >
        <div className="g33-eyebrow" style={{ marginBottom: 10, color: 'var(--g33-or-deep)' }}>
          Export Training Content
        </div>
        <p className="g33-body" style={{ margin: '0 0 18px', color: 'var(--g33-charcoal)', fontSize: 14, lineHeight: 1.6 }}>
          Generate the full 3-day Academy curriculum as a single ZIP containing <strong>PDF</strong> (branded, print-ready), <strong>DOCX</strong> (editable) and <strong>Markdown</strong> (plain text). Images are not embedded — just referenced by code. Use the Image library Export above to back up the illustration files separately.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <button
            type="button"
            onClick={doExportTraining}
            disabled={trainingExporting}
            className="g33-btn-primary"
            data-testid="admin-export-training-btn"
            style={{ padding: '12px 22px', fontSize: 11 }}
          >
            {trainingExporting
              ? <><Loader2 size={14} className="g33-spin" /> Generating ZIP…</>
              : <><FileText size={14} /> Export 3-day program</>}
          </button>
          <span style={{ fontSize: 12, color: 'var(--g33-grey-warm)' }}>
            PDF · DOCX · Markdown · ~500 KB · no images
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', paddingTop: 14, borderTop: '1px solid rgba(15,15,15,0.1)' }}>
          <span style={{ fontSize: 11, color: 'var(--g33-grey-warm)', textTransform: 'uppercase', letterSpacing: '0.16em' }}>
            PDF with images:
          </span>
          {[1, 2, 3].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => doExportPdfDay(d)}
              disabled={pdfExportingDay !== null}
              className="g33-btn-ghost"
              data-testid={`admin-export-pdf-day${d}-btn`}
              style={{ padding: '11px 20px', fontSize: 11 }}
            >
              {pdfExportingDay === d
                ? <><Loader2 size={13} className="g33-spin" /> Generating…</>
                : <><Download size={13} /> Day {d} PDF</>}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--g33-grey-warm)' }}>
            ~2–5 MB each · illustrations embedded
          </span>
        </div>
      </div>

      {/* UPLOAD FORM */}
      <form
        onSubmit={submit}
        className="g33-trainer-block"
        style={{ padding: 32, marginBottom: 48 }}
        data-testid="admin-upload-form"
      >
        <div className="g33-eyebrow" style={{ marginBottom: 16, color: 'var(--g33-or-deep)' }}>
          Upload / replace
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--g33-grey-warm)', textTransform: 'uppercase', letterSpacing: '0.18em', display: 'block', marginBottom: 8 }}>
              Image code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="[GATE33-D01-M01-OPENING-IMG-01]"
              className="g33-modal-input"
              data-testid="admin-code-input"
              style={{ fontFamily: 'ui-monospace, monospace' }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--g33-grey-warm)', textTransform: 'uppercase', letterSpacing: '0.18em', display: 'block', marginBottom: 8 }}>
              Caption (optional)
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Cabin reset · before/after"
              className="g33-modal-input"
              data-testid="admin-caption-input"
            />
          </div>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} data-testid="admin-file-input" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button type="button" onClick={onPick} className="g33-btn-ghost" data-testid="admin-pick-btn">
            <Upload size={14} /> {file ? `Selected: ${file.name}` : 'Pick an image'}
          </button>
          <button
            type="submit"
            className="g33-btn-primary"
            disabled={uploading}
            style={{ padding: '14px 28px', fontSize: 11 }}
            data-testid="admin-upload-btn"
          >
            {uploading ? <><Loader2 size={14} className="g33-spin" /> Uploading…</> : <>Save image</>}
          </button>
        </div>
        {msg && (
          <div
            style={{
              marginTop: 16, padding: '12px 14px',
              background: msg.type === 'success' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(220, 80, 70, 0.08)',
              border: '1px solid',
              borderColor: msg.type === 'success' ? 'rgba(76, 175, 80, 0.35)' : 'rgba(220, 80, 70, 0.35)',
              color: msg.type === 'success' ? '#2e7d32' : '#c62828',
              fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
            data-testid="admin-msg"
          >
            {msg.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}
      </form>

      {/* STATS + REFRESH */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
          <div data-testid="admin-stats">
            <div className="g33-eyebrow" style={{ color: 'var(--g33-or-deep)' }}>Codes detected</div>
            <div className="g33-display" style={{ fontSize: 28, lineHeight: 1 }}>{referenceCodes.length}</div>
          </div>
          <div>
            <div className="g33-eyebrow" style={{ color: 'var(--g33-or-deep)' }}>Uploaded</div>
            <div className="g33-display" style={{ fontSize: 28, lineHeight: 1, color: '#2e7d32' }}>{filledCount}</div>
          </div>
          <div>
            <div className="g33-eyebrow" style={{ color: 'var(--g33-or-deep)' }}>Empty</div>
            <div className="g33-display" style={{ fontSize: 28, lineHeight: 1, color: 'var(--g33-or-deep)' }}>{emptyCount}</div>
          </div>
        </div>
        <button type="button" className="g33-btn-ghost" onClick={refreshImages} data-testid="admin-refresh-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter codes (e.g. D01, M11, AIRCRAFT)"
        className="g33-modal-input"
        style={{ marginBottom: 24 }}
        data-testid="admin-filter-input"
      />

      {/* GRID OF ALL CODES */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 20,
        }}
        data-testid="admin-codes-grid"
      >
        {filteredCodes.map((c) => {
          const entry = images?.[c];
          return (
            <article
              key={c}
              style={{
                background: 'rgba(255, 252, 245, 0.5)',
                border: '1px solid rgba(201, 169, 97, 0.18)',
                padding: 16,
              }}
              data-testid={`admin-card-${c}`}
            >
              {entry ? (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '16 / 10',
                    backgroundImage: `url(${entry.url.startsWith('/') ? `${API}${entry.url}` : entry.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'var(--g33-creme, #f4ede1)',
                    border: '1px solid rgba(201, 169, 97, 0.18)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '16 / 10',
                    background:
                      'repeating-linear-gradient(45deg, rgba(201,169,97,0.06) 0 8px, rgba(201,169,97,0.12) 8px 16px)',
                    border: '1.5px dashed var(--g33-or, #c9a961)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--g33-or-deep)',
                  }}
                >
                  <ImageIcon size={20} />
                </div>
              )}
              <div style={{ marginTop: 12, fontFamily: 'ui-monospace, monospace', fontSize: 11, wordBreak: 'break-all', color: 'var(--g33-noir)' }}>
                {c}
              </div>
              {entry?.caption && (
                <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--g33-grey-warm)', marginTop: 4 }}>
                  {entry.caption}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  className="g33-btn-ghost"
                  style={{ padding: '6px 10px', fontSize: 11 }}
                  onClick={() => selectCode(c)}
                  data-testid={`admin-use-${c}`}
                >
                  {entry ? 'Replace' : 'Upload'}
                </button>
                {entry && (
                  <button
                    type="button"
                    className="g33-btn-ghost"
                    style={{ padding: '6px 10px', fontSize: 11, color: '#c62828' }}
                    onClick={() => onDelete(c)}
                    data-testid={`admin-delete-${c}`}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default AdminImages;
